import { inject, Injector, NgModule, Type } from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { Store, StoreModule } from '@ngrx/store';
import { map, Observable, take } from 'rxjs';
import { CommonLocalizationModule, LocalizationService } from '@capital-access/common/localization';
import {
  DrawerConfig,
  DrawerPosition,
  DrawerRef,
  DrawerType,
  FireflyDrawerModule,
  FireflyDrawerService
} from '@capital-access/firefly/components';
import { NotificationsDrawerActions } from './+store/notifications-drawer.actions';
import { NotificationsDrawerEffects } from './+store/notifications-drawer.effects';
import { notificationsDrawerReducer } from './+store/notifications-drawer.reducer';
import { NOTIFICATIONS_DRAWER_FEATURE_KEY } from './+store/notifications-drawer.state';
import { ActivityNotificationItemComponent } from './components/activity-notification-item/activity-notification-item.component';
import { NotificationItemRendererComponent } from './components/notification-item-renderer/notification-item-renderer.component';
import { NotificationTypeListComponent } from './components/notification-type-list/notification-type-list.component';
import { NotificationsDrawerComponent } from './components/notifications-drawer/notifications-drawer.component';
import { OwnershipNotificationItemComponent } from './components/ownership-notification-item/ownership-notification-item.component';
import { ReportingNotificationItemComponent } from './components/reporting-notification-item/reporting-notification-item.component';
import { NOTIFICATIONS_LOCALIZATION_SCOPE } from './constants/notifications-drawer.constants';
import {
  NOTIFICATION_ITEM_REGISTRY,
  NotificationItemBase,
  NotificationItemRegistry
} from './tokens/notification-item-registry.token';

@NgModule({
  imports: [
    FireflyDrawerModule,
    CommonLocalizationModule.forFeature(NOTIFICATIONS_LOCALIZATION_SCOPE),
    StoreModule.forFeature(NOTIFICATIONS_DRAWER_FEATURE_KEY, notificationsDrawerReducer),
    EffectsModule.forFeature([NotificationsDrawerEffects]),
    NotificationsDrawerComponent,
    NotificationTypeListComponent,
    NotificationItemRendererComponent,
    ActivityNotificationItemComponent,
    OwnershipNotificationItemComponent,
    ReportingNotificationItemComponent
  ],
  providers: [
    {
      provide: NOTIFICATION_ITEM_REGISTRY,
      useValue: new Map<string, Type<NotificationItemBase>>([
        ['activity', ActivityNotificationItemComponent],
        ['ownership-criteria', OwnershipNotificationItemComponent],
        ['reporting', ReportingNotificationItemComponent]
      ]) as NotificationItemRegistry
    }
  ]
})
export class NotificationsDrawerModule {
  private drawerService = inject(FireflyDrawerService);
  private injector = inject(Injector);
  private store = inject(Store);
  private localizationService = inject(LocalizationService);

  openDrawer(): Observable<DrawerRef> {
    return this.localizationService
      .localize('notificationsTitle', {}, { scope: NOTIFICATIONS_LOCALIZATION_SCOPE })
      .pipe(
        take(1),
        map(title => {
          const drawerRef = this.drawerService.openDrawer(
            <DrawerConfig>{
              title,
              position: DrawerPosition.Right,
              type: DrawerType.Info,
              injector: this.injector,
              dataLayoutId: 'notifications-drawer'
            },
            NotificationsDrawerComponent,
            {}
          );
          drawerRef.onClose().subscribe(() => {
            this.store.dispatch(NotificationsDrawerActions.resetNotificationsDrawer());
          });
          return drawerRef;
        })
      );
  }
}
